import { ChangeEvent, FC } from "react";
import { Box, Typography } from "@material-ui/core";
import { FormikBag, FormikProps, withFormik } from "formik";
import * as yup from "yup";

import {
  AllAccessDiscountTooltip,
  Button,
  LabelField,
  MarketplaceMedianLabelField,
  NumberField,
  ROIEquation,
  UsdIntegerInput,
} from "components";
import { getReturnOnInvestment } from "lib/finance";
import { formatCurrencyAmount, formatPercentage } from "lib/format";
import { parseNumber } from "lib/parse";
import {
  BaseAmountSource,
  ListingKind,
  ResaleInfo,
} from "services/listings/types";

export interface FormValues {
  netProceeds: string;
  buyNowPrice: string;
  royxTakeAmount: string;
}

interface FormProps {
  onSave: (values: FormValues) => Promise<void>;
  highestOpenBuyerOfferAmount: number;
  initialValues: FormValues;
  isAcceptingFinalOffersEnabled: boolean;
  isMarketplaceMedianDisabled: boolean;
  listingKind: ListingKind;
  marketplaceMedian: string | null;
  marketplaceMedianMultiplier: number;
  marketplaceMedianSource: BaseAmountSource;
  minimumPrice: number;
  onRemoveBuyNowPrice: () => void;
  resaleInfo: ResaleInfo | null;
  royxTake: number;
}

const EditBuyNowPriceForm: FC<FormProps & FormikProps<FormValues>> = ({
  values,
  errors,
  touched,
  handleBlur,
  handleSubmit,
  setFieldValue,
  isSubmitting,
  isAcceptingFinalOffersEnabled,
  isMarketplaceMedianDisabled,
  listingKind,
  marketplaceMedian,
  marketplaceMedianMultiplier,
  marketplaceMedianSource,
  onRemoveBuyNowPrice,
  resaleInfo,
  royxTake,
}) => {
  const handleUpdateBuyNowPrice = (event: ChangeEvent<HTMLInputElement>) => {
    const buyNowPrice = parseNumber(event.target.value);
    const royxTakeAmount = buyNowPrice * royxTake;
    const netProceeds = buyNowPrice * (1 - royxTake);

    setFieldValue("buyNowPrice", event.target.value, false);
    setFieldValue("royxTakeAmount", String(royxTakeAmount), false);
    setFieldValue("netProceeds", String(netProceeds), false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Typography component="div" gutterBottom>
        <Box fontSize={13}>
          {isAcceptingFinalOffersEnabled
            ? "Enter your list price below. If an investor accepts your list price, a 24-hour auction will begin immediately starting at the list price. The marketplace fee will be automatically subtracted from the highest bid at the end of the auction."
            : "Enter your list price below to make this listing available for immediate sale. The marketplace fee will be automatically subtracted from your list price."}
        </Box>
      </Typography>
      <Box my={2} textAlign="center">
        {!isMarketplaceMedianDisabled && (
          <Box my={2}>
            <MarketplaceMedianLabelField
              marketplaceMedian={marketplaceMedian}
              marketplaceMedianMultiplier={marketplaceMedianMultiplier}
              marketplaceMedianSource={marketplaceMedianSource}
            />
          </Box>
        )}
        <Box my={2}>
          <NumberField
            type="text"
            name="buyNowPrice"
            fullWidth
            label="List Price"
            value={values.buyNowPrice}
            helperText={touched.buyNowPrice && errors.buyNowPrice}
            disabled={isSubmitting}
            onBlur={handleBlur}
            onChange={handleUpdateBuyNowPrice}
            error={touched.buyNowPrice && !!errors.buyNowPrice}
            inputComponent={UsdIntegerInput}
          />
        </Box>
        <Box my={2}>
          <LabelField
            label={`\u2212 Marketplace Fee (${formatPercentage(royxTake)})`}
            value={
              isAcceptingFinalOffersEnabled
                ? `\u2212 ${formatPercentage(royxTake)}`
                : `\u2212${formatCurrencyAmount("USD", values.royxTakeAmount)}`
            }
            tooltipText={
              listingKind !== "direct_listing" ? (
                <AllAccessDiscountTooltip />
              ) : undefined
            }
            interactiveTooltip
          />
        </Box>
        <Box my={2}>
          <LabelField
            label={`= Your ${
              isAcceptingFinalOffersEnabled ? "Minimum " : ""
            }Sale Proceeds`}
            value={`${formatCurrencyAmount("USD", values.netProceeds)}`}
          />
        </Box>
        {!!resaleInfo && !!resaleInfo?.last_update && (
          <Box my={2}>
            <LabelField
              label={`${
                isAcceptingFinalOffersEnabled ? "Minimum " : ""
              }Return On Investment`}
              tooltipText={<ROIEquation />}
              value={formatPercentage(
                getReturnOnInvestment(
                  resaleInfo.royalties_received,
                  parseNumber(values.netProceeds),
                  resaleInfo.purchased_amount,
                  resaleInfo.buyer_fee,
                ),
              )}
            />
          </Box>
        )}
        <Box my={2}>
          <Button
            customColor="green"
            variant="contained"
            size="large"
            id="btn-edit-list-price-modal"
            type="submit"
            fullWidth
            disabled={isSubmitting}
          >
            Edit List Price
          </Button>
        </Box>
        <Box my={2}>
          <Button
            customColor="red"
            variant="contained"
            size="large"
            fullWidth
            onClick={onRemoveBuyNowPrice}
            disabled={isSubmitting}
          >
            Remove List Price
          </Button>
        </Box>
      </Box>
    </form>
  );
};

const formikOptions = {
  mapPropsToValues: (props: FormProps) => props.initialValues,

  validationSchema: (props: FormProps) => {
    const { highestOpenBuyerOfferAmount, minimumPrice } = props;

    return yup.object().shape({
      buyNowPrice: yup
        .string()
        .test(
          "greaterThanListingMinimumPrice",
          `The list price must be greater than the minimum bid (${formatCurrencyAmount(
            "USD",
            minimumPrice,
          )})`,
          function (amount) {
            return amount && parseNumber(amount, 2) < minimumPrice
              ? false
              : true;
          },
        )
        .test(
          "greaterThanHighestOpenBuyerOffer",
          `The list price must be greater than the highest open offer of (${formatCurrencyAmount(
            "USD",
            highestOpenBuyerOfferAmount,
          )})`,
          function (amount) {
            return amount &&
              parseNumber(amount, 2) <= highestOpenBuyerOfferAmount
              ? false
              : true;
          },
        )
        .required("Required"),
    });
  },
  handleSubmit: async (
    values: FormValues,
    { props }: FormikBag<FormProps, FormValues>,
  ) => {
    try {
      await props.onSave(values);
    } catch {
      // pass
    }
  },
};

const EnhancedEditBuyNowPriceForm = withFormik<FormProps, FormValues>(
  formikOptions,
)(EditBuyNowPriceForm);

export default EnhancedEditBuyNowPriceForm;
